import React from 'react';
import {
	Create,
	Datagrid,
	Labeled,
	List,
	TextField,
	DateField,
	SelectField,
	RecordContextProvider,
	ReferenceField,
	ReferenceInput,
	SelectInput,
	TextInput,
	FormDataConsumer,
	SimpleForm,
	Edit,
	EditButton,
	useRecordContext,
	required,
	minLength,
	maxLength,
	email,
	number,
	regex,
	DatagridBody,
	SortButton,
	FilterButton,
	CreateButton,
	ExportButton,
	TopToolbar,
} from 'react-admin';
// eslint-disable-next-line import/no-unresolved
import { saveAs } from 'file-saver';
// eslint-disable-next-line import/no-unresolved
import { CardActions, Grid, useMediaQuery } from '@mui/material';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import {
	AlignmentType,
	Document,
	Packer,
	Paragraph,
	Table,
	TableRow,
	TableCell,
	TextRun,
	WidthType,
} from 'docx';
import { checkCardUniqueness } from '../model/cloud';
import {
	WithMasterPermissions,
} from '../DataProviderExtension/authExtension';


export const statuses = {
	onChecking: 'PROFILE_STATUS_CONFIRMATION',
	confirmed: 'PROFILE_STATUS_CONFIRMED',
	rejected: 'PROFILE_STATUS_UNCONFIRMED',
};

export const profileStatuses = [
	{ id: statuses.onChecking, name: 'на проверке' },
	{ id: statuses.confirmed, name: 'подтвержден' },
	{ id: statuses.rejected, name: 'отклонен' },
];

const validate = ({
	id, profileStatus, card, tradeUnion, confirmationMessage,
}) => {
	const errors = {};

	const cardID = card?.id?.trim();

	if (profileStatus === statuses.confirmed && !cardID) {
		errors.card = { id: 'Заполните номер карты' };
	}

	if (profileStatus !== statuses.confirmed && cardID) {
		errors.card = { id: 'Удалите номер карты, он только для статуса "подтвержден"' };
	}

	if (profileStatus === statuses.rejected && !confirmationMessage) {
		errors.confirmationMessage = 'Напишите о причинах отказа';
	}

	if ((errors.card && errors.card.id) || errors.confirmationMessage || !cardID) {
		return Promise.resolve(errors);
	}

	return checkCardUniqueness(cardID, tradeUnion.id, id)
		.then((isUniq) => (isUniq ? { ...errors, card: { id: 'В базе уже есть такой номер у этого ППО' } } : errors));
};

const parseCardID = (value) => value?.replace(/\s/g, '') || '';

const EditProfileTitle = () => {
	const record = useRecordContext();
	if (!record) {
		return null;
	}
	const titleComponents = [];
	titleComponents.push(record.lastName);
	titleComponents.push(record.firstName);
	titleComponents.push(record.secondName);
	titleComponents.push(record.card && record.card.id);
	return `${titleComponents.filter((c) => c !== undefined).join(' ')}`;
};

export const editProfile = () => {
	return (
		<Edit title={<EditProfileTitle />}>
			<SimpleForm validate={validate}>
				<Labeled title="Фамилия">
					<TextField label="Фамилия" source="lastName" />
				</Labeled>
				<Labeled title="Имя">
					<TextField label="Имя" source="firstName" />
				</Labeled>
				<Labeled title="Отчество">
					<TextField label="Отчество" source="secondName" />
				</Labeled>
				<TextField label="Дата рождения" source="dateOfBirth" />
				<Labeled title="Телефон">
					<TextField label="Телефон" source="phone" />
				</Labeled>
				<Labeled title="e-mail">
					<TextField label="e-mail" source="email" />
				</Labeled>
				<WithMasterPermissions>
					<Labeled title="ППО">
						<ReferenceField label="ППО" source="tradeUnion.id" reference="TradeUnion" link={false}>
							<TextField source="shortTitle" />
						</ReferenceField>
					</Labeled>
				</WithMasterPermissions>
				<SelectInput label="Статус" source="profileStatus" choices={profileStatuses} />
				<TextInput
					label="Номер карты"
					source="card.id"
					format={parseCardID}
				/>
				<FormDataConsumer fullWidth>
					{({ formData, ...rest }) => (formData.profileStatus === statuses.rejected
						&& (
							<TextInput
								label="Основания для отказа"
								source="confirmationMessage"
								multiline
								{...rest}
							/>
						)
					)}
				</FormDataConsumer>
			</SimpleForm>
		</Edit>
	);
};

const profileFilter = [
	<SelectInput
		label="Статус"
		source="profileStatus"
		choices={profileStatuses}
		alwaysOn
	/>,
	<TextInput
		label="Номер карты"
		source="card.id"
		alwaysOn
	/>,
	<TextInput
		label="Фамилия"
		source="lastName"
	/>,
	<TextInput
		label="Имя"
		source="firstName"
	/>,
	<TextInput
		label="Телефон"
		source="phone"
	/>,
	<TextInput
		label="e-mail"
		source="email"
	/>,
	<ReferenceInput label="ППО" source="tradeUnion.id" reference="TradeUnion">
		<SelectInput optionText="shortTitle" />
	</ReferenceInput>,
];

const docxExporter = (userProfiles, fetchRelatedRecords) => {
	fetchRelatedRecords(
		userProfiles.map((v) => ({ tradeUnionId: v.tradeUnion?.id, ...v })),
		'tradeUnionId',
		'TradeUnion',
	)
		.then((tradeUnions) => {
			const options = {};
			const counters = { tradeUnions: {}, statuses: {} };
			userProfiles.forEach((p) => {
				if (p.tradeUnion?.id) {
					counters.tradeUnions[p.tradeUnion?.id] = 1;
				}
				if (p.profileStatus) {
					counters.statuses[p.profileStatus] = 1;
				}
			});
			if (Object.keys(counters.tradeUnions).length > 1) {
				options.tradeUnions = 1;
			}
			if (Object.keys(counters.statuses).length > 1) {
				options.statuses = 1;
			}
			return { options, tradeUnions };
		})
		.then(({ options, tradeUnions }) => {
			const widths = {
				3: [600, 4300, 4200],
				4: [600, 4300, 1700, 2500],
				5: [600, 3300, 1700, 1400, 2100],
			};
			const columns = 3 + Object.keys(options).length;
			const doc = new Document({
				sections: [{
					children: [
						new Paragraph({
							text: `Перечень пользователей на ${
								new Intl.DateTimeFormat('ru').format(new Date())
							}`,
							alignment: AlignmentType.CENTER,
							spacing: {
								after: 200,
							},
						}),
						new Table({
							columnWidths: widths[columns],
							rows: [
								new TableRow({
									children: [
										new TableCell({
											children: [new Paragraph({
												children: [
													new TextRun({ text: '№', bold: true }),
												],
												alignment: AlignmentType.CENTER,
											})],
										}),
										new TableCell({
											children: [new Paragraph({
												children: [
													new TextRun({ text: 'ФИО', bold: true }),
												],
												alignment: AlignmentType.CENTER,
											})],
										}),
										new TableCell({
											children: [new Paragraph({
												children: [
													new TextRun({ text: 'Телефон/E-mail', bold: true }),
												],
												alignment: AlignmentType.CENTER,
											})],
										}),
										options.statuses
											? new TableCell({
												children: [new Paragraph({
													children: [
														new TextRun({ text: 'Статус', bold: true }),
													],
													alignment: AlignmentType.CENTER,
												})],
											})
											: undefined,
										options.tradeUnions
											? new TableCell({
												children: [new Paragraph({
													children: [
														new TextRun({ text: 'ППО', bold: true }),
													],
													alignment: AlignmentType.CENTER,
												})],
											})
											: undefined,
									],
									tableHeader: true,
								}),
								...userProfiles.map((p, i) => (
									new TableRow({
										children: [
											new TableCell({
												children: [new Paragraph({
													text: `${i + 1}`,
												})],
											}),
											new TableCell({
												children: [new Paragraph({
													text: [p.lastName?.trim(), p.firstName?.trim(), p.secondName?.trim()]
														.filter((v) => !!v)
														.join(' '),
												})],
											}),
											new TableCell({
												children: [new Paragraph([p.phone, p.email].filter((v) => !!v).join(' ') || '')],
											}),
											options.statuses
												? new TableCell({
													children: [new Paragraph(profileStatuses.find(
														(s) => s.id === p.profileStatus,
													)?.name || '')],
												})
												: undefined,
											options.tradeUnions
												? new TableCell({
													children: [new Paragraph(tradeUnions[p.tradeUnion?.id]?.shortTitle || '')],
												})
												: undefined,
										],
									})
								)),
							],
							width: {
								size: 9070,
								type: WidthType.DXA,
							},
						}),
					],
				}],
			});
			Packer.toBlob(doc).then((blob) => {
				saveAs(blob, 'Список ППО.docx');
			});
		});
};

const ProfileListRow = ({
	record,
}) => (
	<RecordContextProvider value={record}>
		<Card sx={{ mb: '0.375rem' }}>
			<CardContent>
				<Grid container spacing={1}>
					<Grid item xs={12} sm={4}>
						<TextField label="Фамилия" source="lastName" />
					</Grid>
					<Grid item xs={12} sm={4}>
						<TextField label="Имя" source="firstName" />
					</Grid>
					<Grid item xs={12} sm={4}>
						<TextField label="Отчество" source="secondName" />
					</Grid>
					<Grid item xs={6}>
						<TextField label="Телефон" source="phone" />
					</Grid>
					<Grid item xs={6}>
						<TextField label="e-mail" source="email" />
					</Grid>
					<Grid item xs={12} sm={6}>
						<TextField label="Место работы" source="company" />
					</Grid>
					<WithMasterPermissions>
						<Grid item xs={6}>
							<ReferenceField label="ППО" source="tradeUnion.id" reference="TradeUnion" link={false}>
								<TextField label="ППО" source="shortTitle" />
							</ReferenceField>
						</Grid>
					</WithMasterPermissions>
					<Grid item xs={6} sm={4}>
						<SelectField label="Статус" source="profileStatus" choices={profileStatuses} />
					</Grid>
					<Grid item xs={6} sm={4}>
						<TextField
							label="Номер карты"
							source="card.id"
						/>
					</Grid>
					<Grid item xs={6} sm={4}>
						<DateField label="Дата редактирования" source="updatedAt" showTime />
					</Grid>
				</Grid>
			</CardContent>
			<WithMasterPermissions>
				<CardActions>
					<EditButton />
				</CardActions>
			</WithMasterPermissions>
		</Card>
	</RecordContextProvider>
);

const ProfileListDataGridBody = (props) => <DatagridBody {...props} row={<ProfileListRow />} />;

const ListActions = ({ isSmall }) => {
	return (
		<TopToolbar>
			<FilterButton />
			{isSmall ? <SortButton fields={['lastName', 'firstName', 'secondName', 'phone', 'card.id', 'updatedAt']} /> : null }
			<CreateButton />
			<ExportButton />
		</TopToolbar>
	);
};

export const ListProfile = () => {
	const isSmall = useMediaQuery((theme) => theme.breakpoints.down('md'));
	return (
		<List
			title="Члены ППО"
			sort={{ field: 'updatedAt', order: 'ASC' }}
			filters={profileFilter}
			filterDefaultValues={{ profileStatus: statuses.onChecking }}
			exporter={docxExporter}
			actions={<ListActions isSmall={isSmall} />}
		>
			{
				isSmall
					? (
						<Datagrid
							rowClick="edit"
							header={() => (null)}
							body={<ProfileListDataGridBody />}
						/>
					)
					: (
						<Datagrid rowClick="edit">
							<TextField label="Фамилия" source="lastName" />
							<TextField label="Имя" source="firstName" />
							<TextField label="Отчество" source="secondName" />
							<TextField label="Телефон" source="phone" />
							<TextField label="e-mail" source="email" />
							<TextField label="Место работы" source="company" />
							<TextField
								label="Номер карты"
								source="card.id"
							/>
							<SelectField label="Статус" source="profileStatus" choices={profileStatuses} />
							<WithMasterPermissions>
								<ReferenceField label="ППО" source="tradeUnion.id" reference="TradeUnion" link={false}>
									<TextField label="ППО" source="shortTitle" />
								</ReferenceField>
							</WithMasterPermissions>
							<DateField label="Дата редактирования" source="updatedAt" showTime />
							<WithMasterPermissions>
								<EditButton />
							</WithMasterPermissions>
						</Datagrid>
					)
			}
		</List>
	);
};

const validateLastName = [
	required('Укажите фамилию'),
	minLength(2, 'Введите не менее 2 символов'),
	maxLength(15, 'Введите не более 15 символов'),
	// regex(/^[А-Яа-я-]+$/gi, 'Недопустимые символы'),
];
const validateFirstName = [
	required('Укажите имя'),
	minLength(2, 'Введите не менее 2 символов'),
	maxLength(15, 'Введите не более 15 символов'),
	// regex(/^[А-Яа-я-]+$/gi, 'Недопустимые символы'),
];
const validateSecondName = [
	minLength(2, 'Введите не менее 2 символов'),
	maxLength(15, 'Введите не более 15 символов'),
	// regex(/^[А-Яа-я-]+$/gi, 'Недопустимые символы'),
];
const validateEmail = [
	email('Неверный формат'),
	(value, formValues) => {
		if (!value && !formValues.phone) {
			return 'Укажите номер телефона или email';
		}
		return undefined;
	},
];
const validateCard = [
	required('Укажите номер карты'),
	number('Введите число'),
	minLength(6, 'Минимум 6 цифр'),
];
const validatePhone = [
	regex(/^\+?(7|8)\d{10}$/, 'неверный формат номера'),
	(value, formValues) => {
		if (!value && !formValues.email) {
			return 'Укажите номер телефона или email';
		}
		return undefined;
	},
];

const transformProfile = (data) => {
	return {
		...data,
		profileStatus: statuses.confirmed,
	};
};

export const CreateProfile = () => {
	return (
		<Create transform={transformProfile}>
			<SimpleForm>
				<TextInput label="Фамилия" source="lastName" validate={validateLastName} />
				<TextInput label="Имя" source="firstName" validate={validateFirstName} />
				<TextInput label="Отчество" source="secondName" validate={validateSecondName} />
				<TextInput label="Телефон" source="phone" type="tel" validate={validatePhone} />
				<TextInput label="e-mail" source="email" type="email" validate={validateEmail} />
				<TextInput
					label="Номер карты"
					source="card.id"
					format={parseCardID}
					validate={validateCard}
				/>
				<WithMasterPermissions>
					<ReferenceInput
						label="ППО"
						source="tradeUnion.id"
						reference="TradeUnion"
						sort={{ field: 'shortTitle', order: 'ASC' }}
					>
						<SelectInput label="ППО" optionText="shortTitle" />
					</ReferenceInput>
				</WithMasterPermissions>
			</SimpleForm>
		</Create>
	);
};
